import React, { useState } from 'react';
import { StatusStrip } from '../components/StatusStrip';
import { NavigationBar } from '../components/NavigationBar';
import { HomePage } from '../components/HomePage';
import { VideoPage } from '../components/VideoPage';
import { AudioPage } from '../components/AudioPage';
import { CameraPage } from '../components/CameraPage';
import { AdvancedPage } from '../components/AdvancedPage';
import { SystemState, OperationMode, PartitionState, VideoSource, DisplayMode, CameraMode } from '../types/crestron';
export function CrestronControl() {
  const [activeTab, setActiveTab] = useState('home');
  const [state, setState] = useState<SystemState>({
    power: true,
    mode: OperationMode.Idle,
    partition: PartitionState.Open,
    mainSource: VideoSource.GoogleMeet,
    displayMode: DisplayMode.FollowMain,
    masterVolume: 75,
    zoneTrims: {
      front: 0,
      mid: -3,
      rear: -6
    },
    mics: [{
      id: 'hh1',
      name: 'Handheld 1',
      level: 80,
      muted: true,
      type: 'handheld'
    }, {
      id: 'hh2',
      name: 'Handheld 2',
      level: 80,
      muted: true,
      type: 'handheld'
    }, {
      id: 'hh3',
      name: 'Handheld 3',
      level: 80,
      muted: true,
      type: 'handheld'
    }, {
      id: 'lapel',
      name: 'Lapel',
      level: 85,
      muted: true,
      type: 'lapel'
    }, {
      id: 'podium',
      name: 'Podium',
      level: 75,
      muted: true,
      type: 'podium'
    }],
    cameraMode: CameraMode.Auto,
    presenterTracking: true,
    activeCameraPreset: null,
    autoDucking: true
  });
  const handlePartitionToggle = () => {
    setState(prev => ({
      ...prev,
      partition: prev.partition === PartitionState.Open ? PartitionState.Closed : PartitionState.Open
    }));
  };
  const handleModeSelect = (mode: OperationMode) => {
    let updates: Partial<SystemState> = {
      mode
    };
    switch (mode) {
      case OperationMode.TownhallRemote:
        updates = {
          ...updates,
          mainSource: VideoSource.GoogleMeet,
          displayMode: DisplayMode.FollowMain,
          cameraMode: CameraMode.Manual,
          presenterTracking: false,
          activeCameraPreset: 'stage-wide',
          mics: state.mics.map(m => ({
            ...m,
            muted: true
          }))
        };
        break;
      case OperationMode.TownhallLocal:
        updates = {
          ...updates,
          mainSource: VideoSource.GoogleMeet,
          displayMode: DisplayMode.FollowMain,
          cameraMode: CameraMode.Auto,
          presenterTracking: true,
          activeCameraPreset: null,
          mics: state.mics.map(m => ({
            ...m,
            muted: false
          }))
        };
        break;
      case OperationMode.TownhallHybrid:
        updates = {
          ...updates,
          mainSource: VideoSource.GoogleMeet,
          displayMode: DisplayMode.FollowMain,
          cameraMode: CameraMode.Auto,
          presenterTracking: true,
          mics: state.mics.map(m => ({
            ...m,
            muted: false
          }))
        };
        break;
      case OperationMode.ContentPlayback:
        updates = {
          ...updates,
          mainSource: VideoSource.WallPlate1,
          displayMode: DisplayMode.FollowMain,
          mics: state.mics.map(m => ({
            ...m,
            muted: true
          }))
        };
        break;
      case OperationMode.MeetStream:
        updates = {
          ...updates,
          mainSource: VideoSource.GoogleMeet,
          mics: state.mics.map(m => ({
            ...m,
            muted: false
          }))
        };
        break;
      case OperationMode.Standby:
        updates = {
          ...updates,
          masterVolume: 0,
          displayMode: DisplayMode.Signage,
          mics: state.mics.map(m => ({
            ...m,
            muted: true
          })),
          cameraMode: CameraMode.Manual,
          presenterTracking: false
        };
        break;
    }
    setState(prev => ({
      ...prev,
      ...updates
    }));
  };
  const handleSourceSelect = (source: VideoSource) => {
    setState(prev => ({
      ...prev,
      mainSource: source
    }));
  };
  const handleDisplayModeSelect = (mode: DisplayMode) => {
    setState(prev => ({
      ...prev,
      displayMode: mode
    }));
  };
  const handleMasterVolume = (val: number) => {
    setState(prev => ({
      ...prev,
      masterVolume: val
    }));
  };
  const handleZoneTrim = (zone: 'front' | 'mid' | 'rear', val: number) => {
    setState(prev => ({
      ...prev,
      zoneTrims: {
        ...prev.zoneTrims,
        [zone]: val
      }
    }));
  };
  const handleMicGroupToggle = () => {
    const allMuted = state.mics.every(m => m.muted);
    setState(prev => ({
      ...prev,
      mics: prev.mics.map(m => ({
        ...m,
        muted: !allMuted
      }))
    }));
  };
  const handlePanicMute = (type: 'all' | 'handheld') => {
    if (type === 'all') {
      setState(prev => ({
        ...prev,
        masterVolume: 0,
        mics: prev.mics.map(m => ({
          ...m,
          muted: true
        }))
      }));
    } else {
      setState(prev => ({
        ...prev,
        mics: prev.mics.map(m => m.type === 'handheld' ? {
          ...m,
          muted: true
        } : m)
      }));
    }
  };
  const handleCameraTrackToggle = () => {
    setState(prev => ({
      ...prev,
      cameraMode: !prev.presenterTracking ? CameraMode.Auto : CameraMode.Manual,
      presenterTracking: !prev.presenterTracking,
      activeCameraPreset: null
    }));
  };
  const handlePresetRecall = (preset: string) => {
    setState(prev => ({
      ...prev,
      cameraMode: CameraMode.Manual,
      presenterTracking: false,
      activeCameraPreset: preset
    }));
  };
  const handleMicLevel = (id: string, val: number) => {
    setState(prev => ({
      ...prev,
      mics: prev.mics.map(m => m.id === id ? {
        ...m,
        level: val
      } : m)
    }));
  };
  const handleMicMute = (id: string) => {
    setState(prev => ({
      ...prev,
      mics: prev.mics.map(m => m.id === id ? {
        ...m,
        muted: !m.muted
      } : m)
    }));
  };
  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        return <HomePage currentMode={state.mode} onModeSelect={handleModeSelect} data-id="element-387" />;
      case 'video':
        return <VideoPage currentSource={state.mainSource} displayMode={state.displayMode} partitionState={state.partition} onSourceSelect={handleSourceSelect} onDisplayModeSelect={handleDisplayModeSelect} data-id="element-388" />;
      case 'audio':
        return <AudioPage masterVolume={state.masterVolume} zoneTrims={state.zoneTrims} mics={state.mics} onMasterVolumeChange={handleMasterVolume} onZoneTrimChange={handleZoneTrim} onMicGroupToggle={handleMicGroupToggle} onPanicMute={handlePanicMute} onMicLevelChange={handleMicLevel} onMicMuteToggle={handleMicMute} data-id="element-389" />;
      case 'cameras':
        return <CameraPage cameraMode={state.cameraMode} activePreset={state.activeCameraPreset} onToggleAutoTrack={handleCameraTrackToggle} onRecallPreset={handlePresetRecall} data-id="element-390" />;
      case 'advanced':
        return <AdvancedPage mics={state.mics} autoDucking={state.autoDucking} onMicLevelChange={handleMicLevel} onMicMuteToggle={handleMicMute} onToggleAutoDucking={() => setState(p => ({
          ...p,
          autoDucking: !p.autoDucking
        }))} data-id="element-391" />;
      default:
        return <HomePage currentMode={state.mode} onModeSelect={handleModeSelect} data-id="element-392" />;
    }
  };
  return <div className="w-[1024px] h-[768px] bg-neutral-900 text-white flex flex-col overflow-hidden font-sans mx-auto shadow-2xl" data-id="element-393">
      <StatusStrip state={state} onPartitionToggle={handlePartitionToggle} data-id="element-394" />

      <main className="flex-1 overflow-hidden bg-neutral-800/30 relative" data-id="element-395">
        {renderContent()}
      </main>

      <NavigationBar activeTab={activeTab} onTabChange={setActiveTab} data-id="element-396" />
    </div>;
}